import React, { useState, useEffect, useRef } from 'react';

/* ─── Animated Particle (photon / bit) ─────────────────────────────────── */
function Particle({ color = '#06b6d4', delay = 0, duration = 2.5, top = '50%', label = '' }) {
  const [pos, setPos] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      const animate = (ts) => {
        if (cancelled) return;
        if (!startRef.current) startRef.current = ts;
        const elapsed = (ts - startRef.current) / 1000;
        const progress = (elapsed % duration) / duration;
        setPos(progress * 100);
        rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    }, delay * 1000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [delay, duration]);

  return (
    <div
      style={{
        position: 'absolute',
        top,
        left: `${pos}%`,
        transform: 'translate(-50%, -50%)',
        transition: 'left 0.05s linear',
        zIndex: 10,
      }}
    >
      <div
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: color,
          boxShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
      {label && (
        <span style={{ fontSize: 9, color, whiteSpace: 'nowrap', marginTop: 2, display: 'block', textAlign: 'center' }}>
          {label}
        </span>
      )}
    </div>
  );
}

/* ─── Animated lock icon ───────────────────────────────────────────────── */
function AnimatedLock({ locked = true, color = '#8b5cf6' }) {
  return (
    <div
      className="flex items-center justify-center rounded-full animate-pulse"
      style={{
        width: 48, height: 48,
        background: `radial-gradient(circle, ${color}33, transparent)`,
        border: `2px solid ${color}`,
        fontSize: 24,
      }}
    >
      {locked ? '🔒' : '🔓'}
    </div>
  );
}

/* ─── Section: Classical Cryptography ─────────────────────────────────── */
function ClassicalSection() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStep(s => (s >= 3 ? 0 : s + 1));
    }, 1800);
    return () => clearInterval(id);
  }, []);

  const cipherText = 'A4$h7*#K9@Lz';

  return (
    <div className="rounded-2xl p-6 border border-gray-200 bg-white shadow-sm">

      <h3 className="text-2xl font-bold text-blue-700 mb-2 flex items-center gap-2">
        🔑 <span>Classical Cryptography</span>
        <span className="text-xs font-normal text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-full">Used Everywhere Today</span>
      </h3>
      <p className="text-gray-600 text-sm mb-6">Alice encrypts with a shared secret key → Bob decrypts with the same key</p>

      {/* Alice → Encrypt → Channel → Decrypt → Bob */}
      <div className="relative flex flex-wrap items-start justify-center gap-4 sm:gap-2 mb-6">
        {/* Alice */}
        <div className={`flex flex-col items-center transition-all duration-500 ${step >= 0 ? 'opacity-100' : 'opacity-40'}`}>
          <div className="text-4xl mb-1">👩‍💻</div>
          <div className="text-xs text-gray-700 font-semibold">Alice</div>
          <div className="text-xs text-gray-600 mt-1 bg-gray-50 rounded px-2 py-1 border border-gray-200">
            Hello Friend!
          </div>
        </div>

        {/* Arrow + encrypt */}
        <div className="flex flex-col items-center flex-1 min-w-[100px]">
          <AnimatedLock locked={true} color="#8b5cf6" />
          <div className="text-xs text-gray-600 mt-1">Encrypts</div>
          <div className="text-xs text-gray-500">Secret Key 🗝️</div>
        </div>

        {/* Cipher text on channel */}
        <div className="flex flex-col items-center flex-1 min-w-[140px] relative">
          <div
            className="bg-slate-900 border border-cyan-600 rounded-lg px-3 py-2 text-cyan-300 font-mono text-sm font-bold relative overflow-hidden"
            style={{ boxShadow: '0 0 15px rgba(6,182,212,0.25)' }}
          >
            {cipherText}
            {/* travelling photon-like dot */}
            <div
              className="absolute top-0 left-0 h-full w-2 animate-slide-right opacity-30"
              style={{
                background: 'linear-gradient(90deg, transparent, #06b6d4, transparent)',
                animation: 'slide 2s linear infinite',
              }}
            />
          </div>
          <div className="text-xs text-cyan-700 mt-1">Encrypted Message</div>

          {/* Hacker below channel */}
          <div className="mt-3 flex flex-col items-center">
            <div className="text-2xl">🕵️</div>
            <div className="text-xs text-red-600 font-semibold">Eavesdropper</div>
            <div className="text-xs text-red-700 mt-1 text-center bg-red-50 rounded px-2 py-1 border border-red-200 max-w-[120px]">
              Sees cipher only!<br />
              Tries Key1, Key2...
            </div>
          </div>
        </div>

        {/* Decrypt */}
        <div className="flex flex-col items-center flex-1 min-w-[100px]">
          <AnimatedLock locked={false} color="#06b6d4" />
          <div className="text-xs text-cyan-700 mt-1">Decrypts</div>
          <div className="text-xs text-cyan-600">Same Key 🗝️</div>
        </div>

        {/* Bob */}
        <div className={`flex flex-col items-center transition-all duration-500 ${step >= 3 ? 'opacity-100' : 'opacity-60'}`}>
          <div className="text-4xl mb-1">👨‍💻</div>
          <div className="text-xs text-gray-700 font-semibold">Bob</div>
          <div className={`text-xs mt-1 rounded px-2 py-1 border transition-all duration-700 ${step >= 3 ? 'text-green-700 bg-green-50 border-green-200' : 'text-gray-600 bg-gray-50 border-gray-200'}`}>
            {step >= 3 ? 'Hello Friend! ✓' : '...waiting'}
          </div>
        </div>
      </div>

      {/* Info boxes */}
      <div className="grid grid-cols-2 gap-3 mt-2">
        <div className="rounded-xl p-3 bg-red-50 border border-red-200">
          <div className="text-red-700 font-semibold text-xs mb-1">⚠️ If Hacker Intercepts</div>
          <ul className="text-red-700 text-xs space-y-1">
            <li>• Gets the ciphertext</li>
            <li>• Must brute-force keys</li>
            <li>• Can try billions of combos</li>
            <li>• <span className="text-red-800 font-semibold">No way to detect spy!</span></li>
          </ul>
        </div>
        <div className="rounded-xl p-3 bg-green-50 border border-green-200">
          <div className="text-green-700 font-semibold text-xs mb-1">✅ For Authorised Users</div>
          <ul className="text-green-700 text-xs space-y-1">
            <li>• Correct key → instant decrypt</li>
            <li>• AES, RSA, ECC</li>
            <li>• Fast & widely used</li>
            <li>• <span className="text-green-800 font-semibold">Security = math hardness</span></li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ─── Animated Quantum Channel ─────────────────────────────────────────── */
function QuantumChannel({ hacked }) {
  const photonColors = ['#06b6d4', '#8b5cf6', '#ec4899', '#06b6d4', '#a78bfa'];
  return (
    <div className="relative w-full h-10 rounded-full overflow-hidden"
      style={{
        background: 'linear-gradient(90deg, rgba(6,182,212,0.08), rgba(139,92,246,0.12), rgba(6,182,212,0.08))',
        border: '1px solid rgba(6,182,212,0.3)',
      }}>
      {photonColors.map((c, i) => (
        <Particle key={i} color={hacked ? '#ef4444' : c} delay={i * 0.5} duration={2.5} top="50%" />
      ))}
      <div className="absolute inset-0 flex items-center justify-center text-xs text-cyan-700 font-semibold pointer-events-none select-none">
        Quantum Channel (Photons)
      </div>
    </div>
  );
}

/* ─── Section: Quantum Cryptography (QKD) ─────────────────────────────── */
function QuantumSection() {
  const [hacked, setHacked] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [keyBits, setKeyBits] = useState('10110011');
  const [phase, setPhase] = useState(0); // 0: normal, 1: spy, 2: detected, 3: retry

  const triggerHack = () => {
    setHacked(true);
    setShowAlert(true);
    setPhase(1);
    setTimeout(() => setPhase(2), 1200);
    setTimeout(() => {
      setHacked(false);
      setShowAlert(false);
      setPhase(3);
      setKeyBits('01001101');
    }, 4000);
    setTimeout(() => { setPhase(0); setKeyBits('10110011'); }, 6000);
  };

  return (
    <div className="rounded-2xl p-6 border border-gray-200 bg-white shadow-sm">

      <h3 className="text-2xl font-bold text-purple-700 mb-2 flex items-center gap-2">
        ⚛️ <span>Quantum Cryptography</span>
        <span className="text-xs font-normal text-purple-700 bg-purple-50 border border-purple-200 px-2 py-1 rounded-full">QKD — BB84 Protocol</span>
      </h3>
      <p className="text-gray-600 text-sm mb-5">Quantum Key Distribution uses photons to share a secret key. Any eavesdropping is instantly detectable.</p>

      {/* Step 1: Key Exchange */}
      <div className="mb-5">
        <div className="text-xs font-bold text-cyan-700 mb-3 flex items-center gap-2">
          <span className="bg-cyan-50 border border-cyan-200 rounded-full px-2 py-0.5">STEP 1</span>
          Quantum Key Exchange via Photons
        </div>

        <div className="flex items-center gap-3 mb-3">
          {/* Alice */}
          <div className="flex flex-col items-center min-w-[64px]">
            <div className="text-3xl">👩‍💻</div>
            <div className="text-xs text-gray-700 font-semibold">Alice</div>
            <div className="text-xs text-gray-500 mt-1">Sends photons</div>
          </div>

          {/* Channel */}
          <div className="flex-1 flex flex-col gap-2">
            <QuantumChannel hacked={hacked} />

            {/* Eavesdropper */}
            <div className="flex justify-center">
              <div
                className={`flex flex-col items-center transition-all duration-500 ${hacked ? 'opacity-100 scale-110' : 'opacity-50 scale-100'}`}
              >
                <div className={`text-xl ${hacked ? 'animate-bounce' : ''}`}>🕵️</div>
                <div className={`text-xs font-semibold ${hacked ? 'text-red-600' : 'text-gray-400'}`}>
                  {phase === 0 && 'Eavesdropper (idle)'}
                  {phase === 1 && '⚠️ Intercepting!'}
                  {phase === 2 && '🚨 DETECTED!'}
                  {phase === 3 && '✓ Blocked & retry'}
                </div>
                {hacked && (
                  <div className="text-xs text-red-700 mt-1 bg-red-50 rounded px-2 py-0.5 border border-red-300">
                    Quantum state changed!
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bob */}
          <div className="flex flex-col items-center min-w-[64px]">
            <div className="text-3xl">👨‍💻</div>
            <div className="text-xs text-gray-700 font-semibold">Bob</div>
            <div className="text-xs text-gray-500 mt-1">Receives photons</div>
          </div>
        </div>

        {/* Key result */}
        <div className={`flex items-center justify-center gap-3 p-3 rounded-xl border transition-all duration-700 ${hacked ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
          {hacked ? (
            <span className="text-red-700 font-mono font-bold text-sm animate-pulse">
              🚨 Mismatch detected — discarding key…
            </span>
          ) : (
            <>
              <span className="text-green-700 text-sm">✅ Shared Secret Key:</span>
              <span className="font-mono font-bold text-cyan-700 text-base tracking-widest">{keyBits}</span>
            </>
          )}
        </div>
      </div>

      {/* Step 2: Encrypt with key */}
      <div className="mb-5">
        <div className="text-xs font-bold text-pink-700 mb-3 flex items-center gap-2">
          <span className="bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5">STEP 2</span>
          Use Quantum Key for Normal Encryption (AES)
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <div className="text-xs text-gray-700 bg-gray-50 rounded px-2 py-1 border border-gray-200">Hello Friend!</div>
          <AnimatedLock locked={true} color="#ec4899" />
          <div className="text-xs text-pink-200 font-mono bg-slate-900 rounded px-2 py-1 border border-pink-700">A4$h7*#K9@Lz</div>
          <AnimatedLock locked={false} color="#8b5cf6" />
          <div className="text-xs text-green-700 bg-green-50 rounded px-2 py-1 border border-green-200">Hello Friend! ✓</div>
        </div>
      </div>

      {/* Why powerful */}
      <div className="rounded-xl p-4 bg-purple-50 border border-purple-200 mb-4">
        <div className="text-purple-800 font-semibold text-sm mb-2">💡 Why Quantum Cryptography is Powerful:</div>
        <ul className="space-y-1 text-purple-800 text-xs">
          <li>✅ Anyone trying to steal the key gets <span className="text-cyan-700 font-semibold">detected immediately</span></li>
          <li>✅ <span className="text-pink-700 font-semibold">No-cloning principle:</span> Quantum data cannot be copied perfectly</li>
          <li>✅ Secure key exchange even over <span className="text-amber-700 font-semibold">public networks</span></li>
          <li>✅ Security based on <span className="text-purple-900 font-semibold">laws of physics</span>, not math hardness</li>
        </ul>
      </div>

      {/* Interactive trigger */}
      <button
        onClick={triggerHack}
        disabled={hacked || phase !== 0}
        className="w-full py-3 rounded-xl font-bold text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: hacked
            ? 'linear-gradient(90deg, #dc2626, #991b1b)'
            : 'linear-gradient(90deg, #7c3aed, #0e7490)',
          border: hacked ? '1px solid #ef4444' : '1px solid rgba(139,92,246,0.5)',
          color: '#fff',
          boxShadow: hacked ? '0 0 20px rgba(239,68,68,0.3)' : '0 0 20px rgba(139,92,246,0.25)',
        }}
      >
        {phase === 0 && '🕵️ Simulate Eavesdropping Attack'}
        {phase === 1 && '⚠️ Eavesdropper intercepting photons…'}
        {phase === 2 && '🚨 Spy DETECTED — Quantum state changed!'}
        {phase === 3 && '🔄 Retrying with new secure key…'}
      </button>

      {/* Alert overlay */}
      {showAlert && (
        <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-300 animate-pulse text-center">
          <div className="text-red-700 font-bold text-sm">🚨 Intrusion Detected!</div>
          <div className="text-red-600 text-xs mt-1">
            Alice and Bob noticed a discrepancy in measurement bases.<br />
            The shared key has been discarded. New key exchange initiated.
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Comparison Table ─────────────────────────────────────────────────── */
function ComparisonTable() {
  const rows = [
    ['Security basis', 'Mathematical hardness', 'Laws of quantum physics'],
    ['Eavesdropping', '❌ Undetectable', '✅ Always detectable'],
    ['Key copying', '⚠️ Possible silently', '❌ No-cloning theorem'],
    ['Forward secrecy', '⚠️ Depends on algorithm', '✅ Guaranteed'],
    ['Quantum threat', '❌ Vulnerable to Shor\'s algo', '✅ Quantum-safe by design'],
    ['Examples', 'RSA, AES, ECC', 'BB84 Protocol, E91'],
  ];

  return (
    <div className="rounded-2xl p-6 border border-gray-200 bg-white shadow-sm">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        ⚡ Quick Comparison
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left py-2 px-3 text-gray-500 font-semibold text-xs uppercase tracking-wide w-1/3">Property</th>
              <th className="text-center py-2 px-3 text-blue-700 font-semibold text-xs uppercase tracking-wide">Classical</th>
              <th className="text-center py-2 px-3 text-purple-700 font-semibold text-xs uppercase tracking-wide">Quantum (QKD)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([prop, classical, quantum], i) => (
              <tr
                key={i}
                className="border-t border-gray-100 hover:bg-purple-50 transition-colors"
              >
                <td className="py-2 px-3 text-gray-700 font-medium">{prop}</td>
                <td className="py-2 px-3 text-center text-blue-700">{classical}</td>
                <td className="py-2 px-3 text-center text-purple-700">{quantum}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl p-3 text-center bg-blue-50 border border-blue-200">
          <div className="text-blue-700 font-bold text-sm">Classical</div>
          <div className="text-blue-700 text-xs mt-1">Hard to break — millions of years<br />by classical computers</div>
        </div>
        <div className="rounded-xl p-3 text-center bg-purple-50 border border-purple-200">
          <div className="text-purple-700 font-bold text-sm">Quantum</div>
          <div className="text-purple-700 text-xs mt-1">Impossible to spy without<br />getting caught!</div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main exported page ────────────────────────────────────────────────── */
export default function CryptographyPage() {
  const [tab, setTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: '🔐 Overview', desc: 'Side-by-side comparison' },
    { id: 'classical', label: '🔑 Classical', desc: 'How symmetric encryption works' },
    { id: 'quantum', label: '⚛️ Quantum (QKD)', desc: 'Photon-based key exchange' },
  ];

  return (
    <div className="min-h-screen pt-8 pb-16 px-4 bg-gray-50">

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-5xl font-bold mb-3"
            style={{
              background: 'linear-gradient(90deg, #5C2D91, #ec4899, #0078D4)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            🔐 Quantum Cryptography
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            From classical encryption to quantum key distribution — understand how information stays secret.
          </p>

          {/* Animated bit stream */}
          <div className="flex justify-center gap-1 mt-4 overflow-hidden h-6 relative">
            {Array.from({ length: 40 }).map((_, i) => (
              <span
                key={i}
                className="text-xs font-mono font-bold"
                style={{
                  color: i % 3 === 0 ? '#5C2D91' : i % 3 === 1 ? '#db2777' : '#0078D4',
                  animation: `fadeInOut ${1 + (i % 5) * 0.3}s ease-in-out infinite`,
                  animationDelay: `${i * 0.07}s`,
                  opacity: 0.7,
                }}
              >
                {Math.random() > 0.5 ? '1' : '0'}
              </span>
            ))}
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 text-base ${
                tab === t.id
                  ? 'text-white scale-105 shadow-lg'
                  : 'text-gray-600 hover:text-purple-700 bg-white border border-gray-200 hover:border-purple-200'
              }`}
              style={tab === t.id ? {
                background: 'linear-gradient(90deg, #7c3aed, #0e7490)',
                boxShadow: '0 0 20px rgba(139,92,246,0.35)',
              } : undefined}
            >
              {t.label}
              <span className="block text-xs mt-0.5 opacity-70">{t.desc}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="space-y-6">
          {tab === 'overview' && (
            <>
              <div className="grid md:grid-cols-2 gap-6">
                <ClassicalSection />
                <QuantumSection />
              </div>
              <ComparisonTable />
            </>
          )}
          {tab === 'classical' && (
            <div className="space-y-6">
              <ClassicalSection />
              <div className="rounded-2xl p-6 border border-gray-200 bg-white shadow-sm">
                <h3 className="text-xl font-bold text-blue-700 mb-4">📖 How It Works — Deep Dive</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { icon: '🗝️', title: 'Symmetric Key', desc: 'Both Alice and Bob share the same secret key. If Eve gets this key, all is lost.', color: '#0078D4' },
                    { icon: '🔢', title: 'Public Key (Asymmetric)', desc: 'RSA uses two keys: public to encrypt, private to decrypt. Security = factoring large numbers.', color: '#5C2D91' },
                    { icon: '⚡', title: 'Quantum Threat', desc: "Shor's algorithm on a quantum computer can break RSA in polynomial time!", color: '#db2777' },
                  ].map((c, i) => (
                    <div key={i} className="rounded-xl p-4 border" style={{ borderColor: `${c.color}30`, background: `${c.color}0c` }}>
                      <div className="text-3xl mb-2">{c.icon}</div>
                      <div className="font-bold text-sm mb-1" style={{ color: c.color }}>{c.title}</div>
                      <div className="text-gray-600 text-xs">{c.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {tab === 'quantum' && (
            <div className="space-y-6">
              <QuantumSection />
              <div className="rounded-2xl p-6 border border-gray-200 bg-white shadow-sm">
                <h3 className="text-xl font-bold text-purple-700 mb-4">📖 BB84 Protocol — Step by Step</h3>
                <div className="space-y-3">
                  {[
                    { step: '1', title: 'Alice sends photons', desc: 'Each photon encodes a random bit (0 or 1) with a random polarisation basis (+ or ×).', color: '#5C2D91' },
                    { step: '2', title: 'Bob measures randomly', desc: 'Bob picks a random basis for each photon. He gets the right bit ~50% of the time.', color: '#0078D4' },
                    { step: '3', title: 'Basis reconciliation', desc: 'Over a public channel they compare which bases they used (not the bits themselves).', color: '#db2777' },
                    { step: '4', title: 'Sifting', desc: 'They keep only the bits where bases matched — this is the raw key.', color: '#0d9488' },
                    { step: '5', title: 'Error checking', desc: 'They compare a random subset. A high error rate means someone was eavesdropping!', color: '#d97706' },
                    { step: '6', title: 'Privacy amplification', desc: 'They use hashing to shrink the key, removing any partial information the spy learned.', color: '#7c3aed' },
                  ].map((s, i) => (
                    <div key={i} className="flex gap-3 p-3 rounded-xl border" style={{ borderColor: `${s.color}30`, background: `${s.color}0a` }}>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: s.color, color: '#fff' }}>{s.step}</div>
                      <div>
                        <div className="font-semibold text-sm" style={{ color: s.color }}>{s.title}</div>
                        <div className="text-gray-600 text-xs mt-0.5">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Key insight banner */}
        <div className="mt-8 rounded-2xl p-5 text-center border border-purple-200 bg-gradient-to-r from-purple-50 to-cyan-50">
          <div className="text-gray-900 font-bold text-lg mb-1">💡 In Simple Words</div>
          <div className="text-gray-600 text-sm">
            <span className="text-blue-700 font-semibold">Classical:</span> Hard to break — takes millions of years on classical computers.
            {' '}
            <span className="text-purple-700 font-semibold">Quantum:</span> Impossible to spy without getting caught — physics enforces it!
          </div>
        </div>
      </div>

      {/* CSS keyframes via style tag */}
      <style>{`
        @keyframes fadeInOut {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 1; }
        }
        @keyframes slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(800%); }
        }
      `}</style>
    </div>
  );
}
